import {
  get,
  isApiServiceError,
  post,
  put,
  ApiServiceError,
} from '@classic/Foundation/Services/ApiService'
import { setBetLoopAccessErrorStatus } from '@core/Store/Config'
import { store } from '@core/Store'
import type { ProblemResponse } from '../types'
import { isBetLoopProblemResponse } from '../typeGuards'
import { selectIsBetLoopAccessUnavailable } from '@core/Store/Config/selectors'

export async function getFromBetLoopApi<T>(
  params: Parameters<typeof get>[0],
  options?: { skipGlobalBanCheck?: boolean }
): Promise<T> {
  try {
    const response = await get<T>(params)
    updateAvailabilityOnSuccess()
    return response
  } catch (error) {
    updateAvailabilityOnError(error)
    const response = await processErrorResponse(error as Error)
    if (isBetLoopProblemResponse(response) && !options?.skipGlobalBanCheck) {
      handleIsAccountBannedResponse(response)
    }
    throw response
  }
}

export async function postToBetLoopApi<T>(params: Parameters<typeof post>[0]): Promise<T> {
  try {
    const response = await post<T>(params)
    updateAvailabilityOnSuccess()
    return response
  } catch (error) {
    updateAvailabilityOnError(error)
    const response = await processErrorResponse(error as Error)
    if (isBetLoopProblemResponse(response)) handleIsAccountBannedResponse(response)
    throw response
  }
}

export async function putToBetLoopApi<T>(params: Parameters<typeof put>[0]): Promise<T> {
  try {
    const response = await put<T>(params)
    updateAvailabilityOnSuccess()
    return response
  } catch (error) {
    updateAvailabilityOnError(error)
    const response = await processErrorResponse(error as Error)
    if (isBetLoopProblemResponse(response)) handleIsAccountBannedResponse(response)
    throw response
  }
}

// =============
// Local Helpers
// =============

async function updateAvailabilityOnSuccess() {
  if (selectIsBetLoopAccessUnavailable(store.getState())) {
    store.dispatch(setBetLoopAccessErrorStatus(null))
  }
}

function updateAvailabilityOnError(error: unknown) {
  if (isApiServiceError(error as ApiServiceError)) {
    const status = (error as ApiServiceError).response?.status
    if (typeof status === 'number' && status >= 500 && status <= 599) {
      store.dispatch(setBetLoopAccessErrorStatus('unavailable'))
    }
  } else {
    const isAbort =
      error instanceof DOMException &&
      (error.name === 'AbortError' || error.name === 'TimeoutError')
    if (!isAbort) {
      store.dispatch(setBetLoopAccessErrorStatus('unavailable'))
    }
  }
}

async function processErrorResponse(error: ApiServiceError | Error) {
  if (!isApiServiceError(error)) return error
  const response: ProblemResponse = await error.response.json()
  return response
}

function handleIsAccountBannedResponse(response: ProblemResponse | undefined) {
  if (response?.isBanned) store.dispatch(setBetLoopAccessErrorStatus('banned'))
}
